/**
 * Fake thread detection
 * Identifies spam that mimics legitimate conversations by faking thread context
 */

import type { GmailMessage } from '../types';
import {
  isReplyOrForward,
  getReplyPrefix,
  containsFakeQuotedText,
  normalizeEmail,
  logScoringDecision,
  clampScore,
} from './utils';

export interface FakeThreadResult {
  score: number;
  reasons: string[];
  detected: boolean;
  indicators: string[];
}

/**
 * Detect fake thread patterns in message
 * @param message - Gmail message data
 * @param hasPreviousContact - Whether user has previous contact with sender
 * @param threadHistory - Thread history array
 * @param context - Optional context for logging
 * @returns Score adjustment and reasons
 */
export function detectFakeThread(
  message: GmailMessage,
  hasPreviousContact: boolean,
  threadHistory: string[] = [],
  context?: {
    userId?: string;
    messageId?: string;
  }
): FakeThreadResult {
  const indicators: string[] = [];
  const reasons: string[] = [];
  let score = 0;

  const subject = message.subject || '';
  const snippet = message.snippet || '';
  const fullText = `${subject} ${snippet}`;
  const isReplySubject = isReplyOrForward(subject);
  const replyPrefix = getReplyPrefix(subject);
  const hasThreadHistory = threadHistory.length > 0;

  // CRITICAL: If there's actual thread history, this is a real thread, not a fake one
  // Skip fake thread detection for real threads (threads where trusted domain initiated
  // the conversation and there is a reply are safe)
  if (hasThreadHistory) {
    // Real thread exists - skip fake thread detection
    return {
      score: 0,
      reasons: [],
      detected: false,
      indicators: [],
    };
  }

  // Indicator 1: Subject suggests reply but no thread history
  if (isReplySubject && !hasThreadHistory) {
    const prefix = replyPrefix || 'reply/forward';
    score += 2;
    indicators.push('subject_reply_no_thread');
    reasons.push(`Subject contains "${prefix}" but no thread history exists`);
  }

  // Indicator 2: Subject suggests reply but user never sent in thread
  // (Only applies when there's no thread history - if there's thread history, it's a real thread)
  // This indicator is skipped when there's thread history (handled above)

  // Indicator 3: Subject suggests reply but no previous contact
  // (Only applies when there's no thread history - if there's thread history, it's a real thread)
  if (isReplySubject && !hasPreviousContact && !hasThreadHistory) {
    const prefix = replyPrefix || 'reply';
    score += 1;
    indicators.push('subject_reply_no_contact');
    reasons.push(`Subject contains "${prefix}" but no previous contact with sender`);
  }

  // Indicator 4: Fake quoted text in body
  if (!hasThreadHistory) {
    const quotedTextCheck = containsFakeQuotedText(fullText);
    if (quotedTextCheck.detected) {
      score += 1.5;
      indicators.push('fake_quoted_text');
      reasons.push(
        `Fake quoted text detected (${quotedTextCheck.patterns.join(', ')}) but no thread history`
      );
    }
  }

  // Indicator 5: Multiple fake indicators (stronger signal)
  if (indicators.length >= 2) {
    // Add bonus for multiple indicators
    score += 0.5;
    reasons.push(`Multiple fake thread indicators detected (${indicators.length} total)`);
  }

  // Clamp score to prevent extreme values
  score = clampScore(score, 0, 5);

  const detected = score > 0;

  // Log decision
  if (detected) {
    logScoringDecision({
      userId: context?.userId,
      messageId: context?.messageId,
      senderEmail: normalizeEmail(message.from),
      checkName: 'fake_thread_detection',
      score,
      reason: reasons.join('; '),
    });
  }

  return {
    score,
    reasons,
    detected,
    indicators,
  };
}

/**
 * Detect fake thread with detailed results for trait tracking
 */
export function detectFakeThreadWithDetails(
  message: GmailMessage,
  hasPreviousContact: boolean,
  threadHistory: string[] = [],
  context?: {
    userId?: string;
    messageId?: string;
  }
): FakeThreadResult & {
  details: {
    subject: string;
    isReplySubject: boolean;
    replyPrefix: string | null;
    hasThreadHistory: boolean;
    hasUserSentInThread: boolean;
    quotedTextPatterns: string[];
  };
} {
  const result = detectFakeThread(message, hasPreviousContact, threadHistory, context);
  const subject = message.subject || '';
  const snippet = message.snippet || '';
  const fullText = `${subject} ${snippet}`;
  const quotedTextCheck = containsFakeQuotedText(fullText);

  return {
    ...result,
    details: {
      subject,
      isReplySubject: isReplyOrForward(subject),
      replyPrefix: getReplyPrefix(subject),
      hasThreadHistory: threadHistory.length > 0,
      hasUserSentInThread: threadHistory.includes('user_sent'),
      quotedTextPatterns: quotedTextCheck.patterns,
    },
  };
}
